import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import * as styles from "./SectionBlockMap.css";
import Grid from "~/components/globals/Grid";
import {
  Accordion as SanityAccordion,
  SectionBlockMap as ISectionBlockMap,
} from "~/sanity/types";
import { Accordion } from "~/components/elements/Accordion";
import { Heading } from "~/components/elements/Heading";
import { Paragraph } from "~/components/elements/Paragraph";
import { ButtonLink } from "~/components/elements/ButtonLink";
// @ts-ignore
import ArrowOutward from "@material-design-icons/svg/filled/arrow_outward.svg?react";
import { SVGMapContent } from "~/assets/svgs/SVGMapContent";
import { STATES } from "~/constants";
import { stripSanityStegaFromString } from "~/utils";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useLocation } from "@remix-run/react";
import { IImage } from "~/components/elements/Image";

export type SectionBlockMapProps = ComponentPropsWithoutRef<"div"> & {
  data: ISectionBlockMap;
};

/** Plain state code (e.g. US-CA); preview payloads may include Stega on the string. */
function normalizeMapLocationState(value?: string | null): string | null {
  if (value == null || value === "") return null;
  const cleaned = stripSanityStegaFromString(value);
  return cleaned || null;
}

const toPortableText = (text?: string) => {
  if (!text?.trim()) return undefined;

  return [
    {
      _type: "block" as const,
      _key: "location-description",
      style: "normal" as const,
      markDefs: [],
      children: [
        {
          _type: "span" as const,
          _key: "location-description-span",
          text,
          marks: [],
        },
      ],
    },
  ];
};

export const SectionBlockMap = ({
  data,
  className,
  ...props
}: SectionBlockMapProps) => {
  const defaultScrollTriggerRefresh = useCallback(() => {
    ScrollTrigger.refresh();
  }, []);

  const sectionRef = useRef<HTMLElement | null>(null);
  const mapPinRef = useRef<HTMLDivElement | null>(null);
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const refreshScrollTriggerRef = useRef<() => void>(
    defaultScrollTriggerRefresh,
  );
  const urlLocation = useLocation();
  const mapLocations = useMemo(
    () =>
      (data.locations ?? []) as Array<{
        _key: string;
        state?: string | null;
        description?: string | null;
        partners?: Array<{
          name?: string | null;
          link?: string | null;
          image?: IImage;
          shortDescription?: string;
        }>;
      }>,
    [data.locations],
  );
  const stateTitleByValue = useMemo(
    () => new Map(STATES.map((s) => [s.value, s.title])),
    [],
  );
  const getStateTitle = useCallback(
    (value?: string | null) => {
      const code = normalizeMapLocationState(value);
      return code ? stateTitleByValue.get(code) ?? code : "";
    },
    [stateTitleByValue],
  );

  const locationAccordionItems: SanityAccordion | undefined = useMemo(
    () =>
      mapLocations.map((location) => ({
        _type: "accordionItem",
        _key: location._key,
        item: {
          title: getStateTitle(location.state),
          text: toPortableText(location.description ?? undefined),
          cardArray:
            location.partners && location.partners.length > 0
              ? location.partners.map((partner) => ({
                  image: partner.image,
                  title: partner.name ?? undefined,
                  text: partner.shortDescription ?? undefined,
                  link: partner.link ?? undefined,
                }))
              : undefined,
        },
      })),
    [getStateTitle, mapLocations],
  );
  const listedStateIds = useMemo(
    () =>
      mapLocations.flatMap((location) => {
        const code = normalizeMapLocationState(location.state);
        return code ? [code] : [];
      }),
    [mapLocations],
  );

  const [highlightedStateId, setHighlightedStateId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!mapLocations.length) {
      setHighlightedStateId(null);
    }
  }, [mapLocations.length]);

  useEffect(() => {
    refreshScrollTriggerRef.current();
  }, [urlLocation]);

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);

      const section = sectionRef.current;
      const mapPin = mapPinRef.current;
      const accordion = accordionRef.current;
      if (!section || !mapPin || !accordion) return;

      const mm = gsap.matchMedia();
      mm.add("(min-width: 768px)", () => {
        const delayedRefresh = gsap
          .delayedCall(0.35, ScrollTrigger.refresh)
          .pause();
        const refreshWithAccordionTiming = () => {
          ScrollTrigger.refresh();
          delayedRefresh.restart(true);
        };
        refreshScrollTriggerRef.current = refreshWithAccordionTiming;

        const getMapTopViewportOffset = () =>
          Math.max(0, (window.innerHeight - mapPin.offsetHeight) / 2);
        const getMapBottomViewportOffset = () =>
          Math.min(
            window.innerHeight,
            getMapTopViewportOffset() + mapPin.offsetHeight,
          );

        ScrollTrigger.create({
          trigger: section,
          start: () => `top ${getMapTopViewportOffset()}`,
          endTrigger: accordion,
          end: () => `bottom ${getMapBottomViewportOffset()}`,
          pin: mapPin,
          anticipatePin: 1,
          fastScrollEnd: 3000,
          invalidateOnRefresh: true,
        });

        const resizeObserver = new ResizeObserver(refreshWithAccordionTiming);
        resizeObserver.observe(accordion);
        resizeObserver.observe(mapPin);
        resizeObserver.observe(section);

        return () => {
          refreshScrollTriggerRef.current = defaultScrollTriggerRefresh;
          delayedRefresh.kill();
          resizeObserver.disconnect();
        };
      });

      return () => mm.revert();
    },
    { scope: sectionRef, dependencies: [defaultScrollTriggerRefresh] },
  );

  return (
    <section
      {...props}
      className={clsx(styles.section, className)}
      ref={sectionRef}
    >
      <Grid className={styles.grid}>
        <div className={styles.areaCard}>
          <div className={styles.headingWrapper}>
            <Heading as="h2" size="h3" className={styles.subtitle}>
              {data.subtitle}
            </Heading>
            <Heading as="h3" size="h1" uppercase bold>
              {data.title}
            </Heading>
          </div>
          <Paragraph size="lg">{data.text}</Paragraph>

          {data.link && (
            <div className={styles.buttonContainer}>
              <ButtonLink
                isPrimary={false}
                theme="colour"
                link={data.link}
                icons={{ iconPrimary: <ArrowOutward /> }}
              >
                {data.link.text}
              </ButtonLink>
            </div>
          )}
        </div>
        <div className={styles.areaMap}>
          <div className={styles.mapPin} ref={mapPinRef}>
            <div className={styles.map}>
              <SVGMapContent
                className={styles.mapSvg}
                preserveAspectRatio="xMidYMid meet"
                highlightedStateId={highlightedStateId}
                listedStateIds={listedStateIds}
              />
            </div>
          </div>
        </div>
        <div className={styles.areaAccordion} ref={accordionRef}>
          {locationAccordionItems && locationAccordionItems.length > 0 && (
            <Accordion
              accordionItems={locationAccordionItems}
              defaultOpen={false}
              onValueChange={(value) => {
                if (!value) {
                  setHighlightedStateId(null);
                  refreshScrollTriggerRef.current();
                  return;
                }
                const match = /^item-(\d+)$/.exec(value);
                if (!match) return;
                const stateId = normalizeMapLocationState(
                  mapLocations[Number(match[1])]?.state,
                );
                if (stateId) setHighlightedStateId(stateId);
                refreshScrollTriggerRef.current();
              }}
            />
          )}
        </div>
      </Grid>
    </section>
  );
};
